import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { logConnection, logAction } from '../lib/audit';

const AuthContext = createContext(null);

// Email admin par défaut
const ADMIN_EMAIL = 'tallmoctar@gmail.com';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Fonction pour déterminer le rôle basé sur l'email
const getRoleForEmail = (email) => {
  if (email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
    return 'admin';
  }
  return 'membre';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        // Vérifier d'abord si c'est l'admin par défaut (bypass Firestore)
        const isDefaultAdmin = firebaseUser.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();

        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            // Mettre à jour le rôle si c'est l'admin par défaut
            if (isDefaultAdmin && data.role !== 'admin') {
              try {
                await updateDoc(doc(db, 'users', firebaseUser.uid), { role: 'admin' });
                data.role = 'admin';
              } catch (e) {
                console.log('Could not update role in Firestore, using local override');
                data.role = 'admin';
              }
            }
            setUserData(data);
          } else if (isDefaultAdmin) {
            // Document n'existe pas mais c'est l'admin - créer le document
            const adminData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              fullName: firebaseUser.displayName || 'Administrateur',
              phone: '',
              role: 'admin',
              commission: null,
              cotisationStatus: 'paid',
              joinedAt: serverTimestamp(),
              isActive: true
            };
            try {
              await setDoc(doc(db, 'users', firebaseUser.uid), adminData);
            } catch (e) {
              console.log('Could not create admin doc in Firestore');
            }
            setUserData(adminData);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          // Si erreur Firestore mais c'est l'admin par défaut, on le reconnaît quand même
          if (isDefaultAdmin) {
            setUserData({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              fullName: firebaseUser.displayName || 'Administrateur',
              role: 'admin',
              isActive: true
            });
          }
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const userDoc = await getDoc(doc(db, 'users', result.user.uid));
    let userDataToLog = {};
    if (userDoc.exists()) {
      const data = userDoc.data();
      // Vérifier si c'est l'admin par défaut et mettre à jour le rôle si nécessaire
      if (email?.toLowerCase() === ADMIN_EMAIL.toLowerCase() && data.role !== 'admin') {
        await updateDoc(doc(db, 'users', result.user.uid), { role: 'admin' });
        data.role = 'admin';
      }
      setUserData(data);
      userDataToLog = data;
    }

    // Log connection
    try {
      await logConnection(
        result.user.uid,
        email,
        userDataToLog.fullName || result.user.displayName || email,
        'LOGIN'
      );
      // Also log as a standard audit action for visibility
      await logAction(
        result.user.uid,
        email,
        'LOGIN',
        'Connexion au système',
        { method: 'email_password' }
      );
    } catch (e) {
      console.error("Failed to log connection", e);
    }

    return result;
  };

  const register = async (email, password, fullName, phone, additionalData = {}) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName: fullName });

    // Attribuer automatiquement le rôle admin si c'est l'email admin
    const role = getRoleForEmail(email);

    // Les admins sont automatiquement approuvés, les autres membres doivent être validés
    const isApproved = role === 'admin' ? true : 'pending';

    const newUser = {
      uid: result.user.uid,
      email,
      fullName,
      phone,
      role,
      commission: null,
      cotisationStatus: 'pending',
      joinedAt: serverTimestamp(),
      isActive: true,
      isApproved, // 'pending' pour les nouveaux, true pour les admins
      ...additionalData // Include any additional fields (matricule, address, etc.)
    };

    await setDoc(doc(db, 'users', result.user.uid), newUser);
    setUserData(newUser);

    // Log registration as a login/connection event too
    try {
      await logConnection(result.user.uid, email, fullName, 'REGISTER_LOGIN');
      await logAction(
        result.user.uid,
        email,
        'REGISTER',
        'Inscription nouveau membre',
        { fullName, phone }
      );
    } catch (e) {
      console.error("Failed to log registration connection", e);
    }

    // Notifier les administrateurs
    try {
      const { collection, getDocs, query, where, addDoc } = await import('firebase/firestore');
      const adminsQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
      const adminsSnapshot = await getDocs(adminsQuery);

      const notificationPromises = adminsSnapshot.docs.map(adminDoc =>
        addDoc(collection(db, 'notifications'), {
          userId: adminDoc.id,
          title: 'Nouvelle inscription',
          message: `${fullName} vient de s'inscrire et attend votre validation.`,
          type: 'info',
          link: '/dashboard/members?filter=pending',
          read: false,
          createdAt: serverTimestamp()
        })
      );
      await Promise.all(notificationPromises);
    } catch (error) {
      console.error('Error notifying admins:', error);
    }

    return result;
  };

  const logout = async () => {
    // Log logout before signing out
    if (user) {
      try {
        await logConnection(
          user.uid,
          user.email,
          userData?.fullName || user.displayName || user.email,
          'LOGOUT'
        );
        await logAction(
          user.uid,
          user.email,
          'LOGOUT',
          'Déconnexion du système'
        );
      } catch (e) {
        console.error("Failed to log logout", e);
      }
    }

    await signOut(auth);
    setUser(null);
    setUserData(null);
  };

  const resetPassword = async (email) => {
    const { sendPasswordResetEmail } = await import('firebase/auth');
    try {
      // Log password reset request attempt (we don't have user ID potentially if not logged in, but we have email)
      // We handle this loosely as we might not want to log non-existing users, but assuming email exists context:
      // For now, let's skip specific logging here unless requested, as it's not a "connection".
    } catch (e) { }
    return sendPasswordResetEmail(auth, email);
  };

  const value = {
    user,
    userData,
    loading,
    login,
    register,
    logout,
    resetPassword,
    isAdmin: userData?.role === 'admin',
    isResponsable: userData?.role === 'responsable_commission',
    isApproved: userData?.isApproved === true || userData?.role === 'admin',
    isPending: userData && userData?.isApproved === 'pending' && userData?.role !== 'admin',
    isRejected: userData && userData?.isApproved === false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
